import { AnyObject } from 'final-form';
import i18n from 'i18next';
import { format, isAfter, isBefore, isValid, parse, parseISO } from 'date-fns';

import { AdditionalTravellerFieldEnum, FieldsMap, InputType, PassengerField } from './types';
import {
	API_DATE_FORMAT,
	DATE_REGEXP,
	decapitalizeFirstLetter,
	getPassengerTypeLabel,
	isExistI18nTranslate
} from '../utils';
import { TravellerFields } from '../TravellerForm/types';
import { DeepPartial } from '../types';
import {
	AviaPassengerType,
	ChangeValidationRuleFragment,
	FfpAccountFieldEnum,
	TravellerFieldEnum,
	ValidationRuleFragment
} from '@websky/graphql';
import { clearPhoneNumber } from '../PhoneInput/utils';
import { levenshtein } from '../levenshtein';
import { AutocompleteOption } from '../BaseComponents/Dropdown/types';

export const fieldsArrayToMap = (fields: PassengerField[]): FieldsMap => {
	const map: FieldsMap = {};

	fields.map(field => {
		if (!map.hasOwnProperty(field.name)) {
			map[field.name] = field;
		}
	});

	return map;
};

export const hideText = (text: string, divider = 1.5): string => {
	if (!text) {
		return '';
	}

	const numOfVisibleChars = Math.floor(text.length / divider);
	const numOfHiddenChars = text.length - numOfVisibleChars;

	return text.split('').fill('*', 0, numOfHiddenChars).join('');
};

export const getAdditionalLabel = (
	values: AnyObject,
	fieldsMap: FieldsMap,
	passengerType: AviaPassengerType,
	supplierType?: string
) => {
	const labels: string[] = [];
	let personalInfo = '';

	if (values[TravellerFields.FirstName]) {
		personalInfo = values[TravellerFields.FirstName];

		if (values[TravellerFields.LastName]) {
			personalInfo += ` ${values[TravellerFields.LastName]}`;
		}
	} else {
		personalInfo = getPassengerTypeLabel(passengerType, supplierType);
	}

	if (values[TravellerFields.BirthDate]) {
		personalInfo += `, ${values[TravellerFields.BirthDate]}`;
	}

	labels.push(personalInfo);

	if (values.hasOwnProperty('loyaltyName') && fieldsMap['loyaltyName'].options) {
		const option = fieldsMap['loyaltyName'].validations[0].options.find(
			option => option.value === values['loyaltyName']
		);

		if (option) {
			labels.push(option.value);
		}
	}

	if (values.hasOwnProperty('nationality') && fieldsMap['nationality'].validations[0].options) {
		const option = fieldsMap['nationality'].validations[0].options.find(
			option => option.value === values['nationality']
		);

		if (option) {
			labels.push(option.label);
		}
	}

	if (
		values.hasOwnProperty('docType') &&
		values.hasOwnProperty('docNumber') &&
		fieldsMap['docType'].validations[0].options
	) {
		fieldsMap['docType'].validations.forEach(validation => {
			const option = validation.options.find(option => option.value === values['docType']);
			if (option) {
				labels.push(i18n.t(`Passenger:${option.label}`));
				labels.push(hideText(values['docNumber']));
			}
		});
	}

	return labels.join(' - ');
};

export const formatInvalidPassengerDate = (fieldType: TravellerFieldEnum, value: string): string => {
	if (fieldType === TravellerFieldEnum.BirthDate || fieldType === TravellerFieldEnum.DocExpiryDate) {
		const parsedDate = parse(value, API_DATE_FORMAT, new Date());

		return isValid(parsedDate) ? format(parsedDate, 'dd.MM.yyyy') : value;
	}

	return value;
};

export const parseDate = (date: string): Date => {
	const isInvalidDateFormat = date.match(DATE_REGEXP);
	const validDate = isInvalidDateFormat ? date.split('-').reverse().join('.') : date;

	return parse(validDate, 'dd.MM.yyyy', new Date());
};

export const validateDate = (value: string, rule: DeepPartial<ValidationRuleFragment>): string | void => {
	const date = parseDate(value);

	if (!isValid(date) || value.length !== 'dd.MM.yyyy'.length) {
		return 'Enter date in dd.mm.yyyy format';
	}

	if (rule.maxDate && isAfter(date, parseISO(rule.maxDate))) {
		return 'Invalid date format';
	}

	if (rule.minDate && isBefore(date, parseISO(rule.minDate))) {
		return 'Invalid date format';
	}
};
/**
 * Finds acceptable validation rules based on all values on the form object.
 * Acceptable is a rule where all dependencies in `with` property are
 * contains in allValues and all values is equals.
 *
 * @param allValues
 * @param validations
 */
export const findAcceptableRules = (allValues: object, validations: DeepPartial<ValidationRuleFragment>[]) => {
	let rules: DeepPartial<ValidationRuleFragment>[] = [];

	validations.forEach(validation => {
		if (!validation.with) {
			rules.push(validation);
		} else if (
			validation.with.every(({ value, name }) => {
				const valueRegex = new RegExp(`^${value}$`);
				const valueByName = allValues[decapitalizeFirstLetter(name)] ?? '';

				return valueRegex.test(valueByName);
			})
		) {
			rules = setValidation(rules, validation);
		}
	});

	return rules;
};

export const findSortedAcceptableRules = (allValues: object, validations: DeepPartial<ValidationRuleFragment>[]) => {
	return findAcceptableRules(allValues, validations).sort((a, b) => {
		if (!a.with) {
			return 1;
		} else if (!b.with) {
			return -1;
		}

		return b.with.length - a.with.length;
	});
};

/**
 * In cases when allRules array contains a rule where `with` fields is greater than new `rule.with`, a new rule will be ignored.
 * Likewise, when `rule.with` fields is greater than some item in `allRules` array, old rule will be replaced with a `rule`.
 *
 * A is greater than B, when  A.length > B.length and A contains all items from B.
 * @param allRules validation rules array
 * @param newRule new rule to add
 */
const setValidation = (
	allRules: DeepPartial<ValidationRuleFragment>[],
	newRule: DeepPartial<ValidationRuleFragment>
) => {
	const newDependencies = newRule.with.map(option => option.name);

	let shouldToAdd = true;

	const rules = allRules.filter(_rule => {
		if (!_rule.with) {
			return true;
		}

		// Finding a rule is greater than a new
		if (
			_rule.with.length > newDependencies.length &&
			newDependencies.every(optionName => _rule.with.some(option => option.name === optionName))
		) {
			// Found a greater rule, ignore a new
			shouldToAdd = false;
		}

		if (
			newDependencies.length > _rule.with.length &&
			_rule.with.every(option => newDependencies.includes(option.name))
		) {
			// A new rule is greater than in array. Remove old rule
			return false;
		}

		return true;
	});

	if (shouldToAdd) {
		return [...rules, newRule];
	}

	return allRules;
};

export const isChangeValidationRule = (
	rule: DeepPartial<ValidationRuleFragment | ChangeValidationRuleFragment>
): rule is ChangeValidationRuleFragment => {
	return (rule as ChangeValidationRuleFragment).maxEditDistance !== undefined;
};

export const validate = (
	value: string | boolean | AutocompleteOption,
	allValues: object,
	inputType: InputType,
	validations: DeepPartial<ValidationRuleFragment | ChangeValidationRuleFragment>[] = [],
	fieldType?: TravellerFieldEnum | AdditionalTravellerFieldEnum | FfpAccountFieldEnum,
	defaultValue?: string,
	allPassengersValues?: object,
	ns?: string | string[]
): string | void => {
	if (validations && validations.length) {
		const rules = findAcceptableRules(allValues, validations);

		for (const rule of rules) {
			const isEmptyString = typeof value === 'string' && !value?.trim()?.length;
			const isEmptyObject = typeof value === 'object' && !value?.value.trim()?.length;
			const isFalsy = !value;

			// Check if field is required.
			if (rule.required && (isEmptyString || isEmptyObject || isFalsy)) {
				return 'Required';
			}

			if (value && typeof value === 'string') {
				// Check that date is valid.
				if (inputType === InputType.Date) {
					const error = validateDate(value, rule);

					if (error) {
						return error;
					}
				}

				// Levenshtein distance check
				if (isChangeValidationRule(rule) && rule.maxEditDistance !== null && defaultValue) {
					const distance = levenshtein(defaultValue, value);

					if (distance > rule.maxEditDistance) {
						return 'Exceeded the number of editable characters';
					}
				}

				if (
					fieldType === AdditionalTravellerFieldEnum.OrganizationName ||
					fieldType === AdditionalTravellerFieldEnum.PersonnelNumber
				) {
					if (allValues['organizationName'] === allValues['personnelNumber']) {
						return 'Check if the input is correct';
					}
				}

				if (rule.regExp) {
					const cleanValue = inputType === InputType.Phone ? clearPhoneNumber(value) : value;
					const failedRule = rule.regExp.find(({ pattern }) => !new RegExp(pattern).test(cleanValue));

					if (failedRule) {
						if (inputType === InputType.Phone) {
							return 'Incorrect phone number';
						}

						if (fieldType) {
							const customFieldError = `${fieldType}.${failedRule.error}`;

							if (isExistI18nTranslate(customFieldError, { ns })) {
								return customFieldError;
							}
						}

						return failedRule.error;
					}
				}

				if (fieldType === TravellerFieldEnum.LinkedTraveller) {
					const linkedPassenger = allPassengersValues[value];

					const birthDate =
						linkedPassenger?.birthDate?.length === 10
							? parse(linkedPassenger.birthDate, 'dd.MM.yyyy', new Date())
							: null;

					const minDate = rule.minDate ? new Date(new Date(rule.minDate).setHours(0, 0, 0, 0)) : null;

					if (birthDate && minDate && isAfter(birthDate, minDate)) {
						return 'The chaperone must be over 18 years of age';
					}
				}
			}
		}
	}
};

export const fieldIsRequired = (allValues: object, validations: Partial<ValidationRuleFragment>[] = []): boolean => {
	if (validations?.length) {
		for (const rule of validations) {
			if (
				(!rule.with ||
					rule.with.every(({ name, value }) =>
						new RegExp(`^${value}$`).test(allValues[decapitalizeFirstLetter(name)])
					)) &&
				rule.required
			) {
				return true;
			}
		}
	}

	return false;
};

export const getRegExpMaxLength = (regexStr: string) => {
	const maxRegex = /{\d+(,\d+)?}/g;

	return (
		regexStr.match(maxRegex)?.reduce((total, match) => {
			const numbers = match.replace(/([{}])/g, '').split(',') ?? [];
			const maxNumber = Number(numbers[numbers.length - 1]);

			return total + (!isNaN(maxNumber) ? maxNumber : 0);
		}, 0) ?? null
	);
};

export const getFieldRegExpMaxLength = (
	allValues: object,
	validations: ValidationRuleFragment[] = []
): number | undefined => {
	if (!validations.length) {
		return undefined;
	}

	const rulePattern = findAcceptableRules(allValues, validations)?.[0]?.regExp?.[0]?.pattern;
	if (!rulePattern) {
		return undefined;
	}

	return getRegExpMaxLength(rulePattern) || undefined;
};
